/**
 * Ravana Agent Chat Service
 * Replaces n8n webhook integration with custom AI agent
 */

const AGENT_BASE_URL = process.env.REACT_APP_AGENT_URL || 'https://ravana-mig-agent.vercel.app';

/**
 * Normalize agent response to match expected format
 */
const normalizeResponse = (payload) => {
    if (!payload) {
        return { reply: 'The agent returned an empty response.' };
    }

    if (typeof payload === 'string') {
        return { reply: payload.trim() || 'The agent returned an empty response.' };
    }

    // Agent returns { response, sessionId, provider }
    const directText = payload.response || payload.reply || payload.text || payload.message;

    if (directText) {
        return {
            reply: typeof directText === 'string' ? directText : JSON.stringify(directText, null, 2),
            sessionId: payload.sessionId,
            suggestions: Array.isArray(payload.suggestions) ? payload.suggestions : undefined,
        };
    }

    return {
        reply: JSON.stringify(payload, null, 2),
        sessionId: payload.sessionId,
    };
};

/**
 * Send a message to the Ravana Agent webhook
 * @param {Object} options - Request options
 * @param {string} options.message - User message text
 * @param {string} options.sessionId - Session identifier
 * @param {AbortSignal} options.signal - Optional abort signal
 * @param {Object} options.extraParams - Additional parameters (cv_text, filename, etc.)
 * @returns {Promise<{reply: string, sessionId?: string, suggestions?: Array}>}
 */
export const sendMessageToAgent = async ({ message, sessionId, signal, extraParams = {} }) => {
    if (!message || !message.trim()) {
        throw new Error('Message is required.');
    }

    // Build URL with sessionId as query param
    const webhookUrl = new URL(`${AGENT_BASE_URL}/webhook`);
    if (sessionId) {
        webhookUrl.searchParams.set('sessionId', sessionId);
    }

    // Build request body
    const body = {
        message: message.trim(),
    };

    // Add CV data if provided
    if (extraParams.cv_text || extraParams.cv_summary) {
        body.cv_text = extraParams.cv_text || extraParams.cv_summary;
    }
    if (extraParams.cv_filename || extraParams.filename) {
        body.filename = extraParams.cv_filename || extraParams.filename;
    }
    if (extraParams.cv_url) {
        body.cv_url = extraParams.cv_url;
    }

    try {
        const response = await fetch(webhookUrl.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
            signal,
        });

        let payload;
        const contentType = response.headers.get('content-type') || '';

        if (contentType.includes('application/json')) {
            payload = await response.json();
        } else {
            const text = await response.text();
            try {
                payload = JSON.parse(text);
            } catch {
                payload = text;
            }
        }

        if (!response.ok) {
            const errorMessage =
                typeof payload === 'string'
                    ? payload
                    : payload?.message || payload?.error || 'Unexpected error from agent.';
            throw new Error(errorMessage);
        }

        return normalizeResponse(payload);
    } catch (error) {
        if (error.name === 'AbortError') {
            throw error;
        }
        throw new Error(error.message || 'Failed to connect to Ravana Agent.');
    }
};

export default sendMessageToAgent;
